/**
 * /fair-launch/create — Fair Launch creation flow.
 *
 * Mirrors the Option 1 Create.tsx pattern:
 *   connect → configure → review → deploy → result
 *
 * Deployment calls FairLaunchFactory.createFairLaunch(name, symbol)
 * via the connected MetaMask wallet. The creation fee is read from
 * the contract; fee-exempt callers pay 0.
 *
 * The graduation threshold is NOT a fixed ETH amount — it is computed
 * from a Chainlink oracle read at market creation time (D-18). The
 * parameters shown here use a $2,000/ETH reference price; the actual
 * on-chain values are locked when the user signs the transaction.
 */

import { useState, useMemo, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { parseEventLogs } from "viem";
import { useWallet } from "../wallet/WalletContext";
import { Button, Card, Field, Pill, inputClass } from "../components/ui";
import { AddEthPanel } from "../components/AddEthPanel";
import { WalletButton } from "../components/WalletButton";
import { validateName, validateSymbol, validateUrl } from "../lib/validation";
import { validateDescription, MAX_DESCRIPTION_CHARS } from "../lib/metadata";
import { byteLength, MAX_NAME_BYTES, MAX_SYMBOL_BYTES } from "../config/limits";
import { ACTIVE_CHAIN } from "../config/chains";
import { fairLaunchFactoryAbi } from "../config/fairLaunchFactoryAbi";
import { bondingCurveMarketAbi } from "../config/bondingCurveMarketAbi";
import { useTradeRecord, persistTradeStatus } from "../hooks/useTradeRecord";

// ── Minimal Chainlink aggregator ABI (oracle pre-flight check) ────────────────
const aggregatorAbi = [
  {
    name: "latestRoundData",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [
      { name: "roundId",         type: "uint80"  },
      { name: "answer",          type: "int256"  },
      { name: "startedAt",       type: "uint256" },
      { name: "updatedAt",       type: "uint256" },
      { name: "answeredInRound", type: "uint80"  },
    ],
  },
] as const;

// ── Curve constants (mirrors FairLaunchFactory.sol testnet deployment) ───────
//
// TESTNET values (testnetScaleFactor = 100):
//   graduationValuationUsd = $500 USD   (mainnet would be $50,000)
//   virtualReserveUsd      = $30  USD   (mainnet would be ~$3,000)
//
// At the MockV3Aggregator price of $2,000/ETH:
//   VIRTUAL_ETH  = $30  / $2000  = 0.015 ETH
//   GRAD_THRESH  ≈ (sqrt(500 × 30 × 0.8) − 30) / 2000 ≈ 0.040 ETH
//
// These are used ONLY for the curve-preview SVG and the display table.
// The actual per-market ETH threshold is computed from the Chainlink oracle
// at deploy time and stored immutably on-chain — it cannot be changed later.

const TOTAL_SUPPLY       = 1_000_000_000n;  // whole tokens
const CURVE_ALLOC_PCT    = 80;
const MIGRATION_PCT      = 20;
const VIRTUAL_ETH        = 0.015;           // ETH at $2k/ETH testnet oracle (mainnet: ~1.5)
const GRAD_THRESHOLD     = 0.040;           // ETH at $2k/ETH testnet oracle (mainnet: ~3.977)
const GRAD_THRESHOLD_USD = 500;             // USD graduation target (testnet ÷100 of $50k mainnet)
const BUY_FEE_BPS        = 100;             // 1%
const SELL_FEE_BPS       = 100;             // 1%
const LOCK_DAYS          = 30;

// ── Curve maths helpers ───────────────────────────────────────────────────────

function calcInitialPrice() {
  const V = VIRTUAL_ETH;
  const T = (CURVE_ALLOC_PCT / 100) * Number(TOTAL_SUPPLY);
  return V / T;
}

function calcPriceAtGraduation() {
  const V = VIRTUAL_ETH, R = GRAD_THRESHOLD;
  const C = (CURVE_ALLOC_PCT / 100) * Number(TOTAL_SUPPLY);
  const T = (V * C) / (V + R);
  return (V + R) / T;
}

function calcTokensSoldAtGraduation() {
  const V = VIRTUAL_ETH, R = GRAD_THRESHOLD;
  const C = (CURVE_ALLOC_PCT / 100) * Number(TOTAL_SUPPLY);
  const T = (V * C) / (V + R);
  return ((C - T) / C) * 100;
}

// ── Price formatting helper ───────────────────────────────────────────────────
// Converts a Chainlink raw price to a display string e.g. "$2,000.00 / ETH".
// `decimals` defaults to 8 (standard ETH/USD feed) but should be passed from
// the on-chain oracleDecimals() value so the display stays correct if the feed
// is ever changed to a non-8-decimal source (Task #297).
// Used on both the review stage oracle banner and the post-deploy success card.
function formatFallbackPrice(raw: bigint, decimals = 8): string {
  return `$${(Number(raw) / 10 ** decimals).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} / ETH`;
}

// ── Bonding curve SVG preview ─────────────────────────────────────────────────

function CurvePreview({ ethRaisedPreview }: { ethRaisedPreview: number }) {
  const W = 280, H = 130, PAD = 24;
  const innerW = W - PAD * 2, innerH = H - PAD * 2;

  type Pt = [number, number];
  const points: Pt[] = [];
  const maxPrice = calcPriceAtGraduation() * 1.05;

  for (let i = 0; i <= 80; i++) {
    const R = (i / 80) * GRAD_THRESHOLD;
    const T = (VIRTUAL_ETH * (CURVE_ALLOC_PCT / 100) * Number(TOTAL_SUPPLY)) / (VIRTUAL_ETH + R);
    const price = (VIRTUAL_ETH + R) / T;
    const x = PAD + (R / GRAD_THRESHOLD) * innerW;
    const y = H - PAD - Math.min(price / maxPrice, 1) * innerH;
    points.push([x, y]);
  }
  const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const previewX = PAD + Math.min(ethRaisedPreview / GRAD_THRESHOLD, 1) * innerW;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Price curve preview" className="w-full">
      <defs>
        <linearGradient id="fg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#00e5c7" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#00e5c7" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="rgba(0,229,199,0.15)" strokeWidth="1" />
      <line x1={PAD} y1={PAD} x2={PAD} y2={H - PAD} stroke="rgba(0,229,199,0.15)" strokeWidth="1" />
      <line x1={W - PAD} y1={PAD} x2={W - PAD} y2={H - PAD}
        stroke="rgba(0,229,199,0.3)" strokeWidth="1" strokeDasharray="3 3" />
      <text x={W - PAD + 2} y={PAD + 10} fill="rgba(0,229,199,0.5)" fontSize="7.5" fontFamily="monospace">
        ~$50k
      </text>
      <path d={`${pathD} L${(W - PAD).toFixed(1)},${(H - PAD).toFixed(1)} L${PAD},${(H - PAD).toFixed(1)} Z`}
        fill="url(#fg)" />
      <path d={pathD} fill="none" stroke="#00e5c7" strokeWidth="1.5" strokeLinecap="round" />
      {ethRaisedPreview > 0 && (
        <line x1={previewX} y1={PAD} x2={previewX} y2={H - PAD}
          stroke="#12a3ff" strokeWidth="1.5" strokeDasharray="3 3" />
      )}
      <text x={W / 2} y={H - 4} fill="rgba(200,210,230,0.35)" fontSize="7" textAnchor="middle" fontFamily="monospace">
        ETH raised →
      </text>
    </svg>
  );
}

// ── Stage types ───────────────────────────────────────────────────────────────

type Stage = "connect" | "configure" | "review" | "deploy";
const STAGES: Stage[] = ["connect", "configure", "review", "deploy"];

type DeployState =
  | { status: "idle" }
  | { status: "estimating" }
  | { status: "signing" }
  | { status: "confirming"; hash: `0x${string}` }
  | { status: "success"; tokenAddress: `0x${string}`; marketAddress: `0x${string}`; hash: `0x${string}` }
  | { status: "error"; message: string };

/**
 * Oracle status detected by the pre-flight check:
 *  - "checking"  — request in flight
 *  - "live"      — oracle responded with a valid, fresh price
 *  - "fallback"  — oracle is down / stale, but owner has set a fallback price
 *                  (market will be created using the fallback; oracleRoundId will be 0)
 *  - "down"      — oracle is down AND no fallback is configured (creation will revert)
 *  - "unknown"   — could not read factory state (RPC error); we don't block the UI
 */
type OracleStatus = "checking" | "live" | "fallback" | "down" | "unknown";

// ── Page ──────────────────────────────────────────────────────────────────────

export default function FairLaunchCreate() {
  const navigate = useNavigate();
  const { address, isConnected, chainId, balance, publicClient, walletClient } = useWallet();
  const [showAddEthCreate, setShowAddEthCreate] = useState(false);

  // ── Single one-click disclosure acknowledgment ────────────────────────────
  // Stored in sessionStorage so it survives multi-step navigation within the
  // session but resets automatically when the browser session ends.
  const [flAckAccepted, setFlAckAccepted] = useState<boolean>(() => {
    try { return sessionStorage.getItem("synapz_fl_create_ack") === "true"; } catch { return false; }
  });

  const [stage, setStage]         = useState<Stage>("connect");
  const [name, setName]           = useState("");
  const [symbol, setSymbol]       = useState("");
  const [description, setDescription] = useState("");
  const [website, setWebsite]     = useState("");
  const [x, setX]                 = useState("");
  const [telegram, setTelegram]   = useState("");
  const [deployState, setDeployState] = useState<DeployState>({ status: "idle" });

  // Read creation fee from contract (may vary by caller due to fee exemption)
  const [creationFeeWei, setCreationFeeWei] = useState<bigint | null>(null);

  // Oracle pre-flight status
  const [oracleStatus, setOracleStatus] = useState<OracleStatus>("checking");

  // Timestamp (ms) of when the last oracle poll completed successfully.
  // null while the very first check is still in-flight.
  const [oracleLastCheckedAt, setOracleLastCheckedAt] = useState<number | null>(null);

  // Live "seconds since last check" counter — updated every second.
  const [secondsSinceOracleCheck, setSecondsSinceOracleCheck] = useState<number>(0);

  // Raw fallback price from the factory (Chainlink format, decimals from oracleDecimals()).
  // Only populated when oracleStatus === "fallback".
  // Displayed as "$2,000.00/ETH" on the review page (Task #143).
  const [fallbackPriceRaw, setFallbackPriceRaw] = useState<bigint | null>(null);

  // Decimals returned by factory.oracleDecimals() — fetched alongside the fallback price.
  // Defaults to 8 (standard Chainlink ETH/USD) until the contract confirms otherwise.
  // Using the on-chain value instead of a hard-coded 1e8 ensures correctness if the
  // oracle feed ever changes to a non-8-decimal source (e.g. 18-decimal feed).
  const [fallbackPriceDecimals, setFallbackPriceDecimals] = useState<number>(8);

  // After a successful deploy, read oracleRoundId from the deployed market
  // to confirm whether the fallback path was used (roundId === 0)
  const [deployedOracleRoundId, setDeployedOracleRoundId] = useState<bigint | null>(null);

  // Snapshot of the fallback price captured at the moment of confirmed deployment.
  // Using a snapshot (rather than the live-polled fallbackPriceRaw) ensures the
  // success screen always shows the price that was actually used, even if the oracle
  // recovers and clears fallbackPriceRaw after the tx is confirmed (Task #296).
  const [deployedFallbackPriceRaw, setDeployedFallbackPriceRaw] = useState<bigint | null>(null);
  const [deployedFallbackPriceDecimals, setDeployedFallbackPriceDecimals] = useState<number>(8);

  const factory = ACTIVE_CHAIN.fairLaunchFactoryAddress;

  useEffect(() => {
    if (!factory) return;
    let cancelled = false;
    publicClient.readContract({
      address: factory as `0x${string}`,
      abi: fairLaunchFactoryAbi,
      functionName: "creationFee",
    }).then(async (fee) => {
      if (cancelled) return;
      // Check if caller is fee-exempt
      if (address) {
        try {
          const exempt = await publicClient.readContract({
            address: factory as `0x${string}`,
            abi: fairLaunchFactoryAbi,
            functionName: "feeExempt",
            args: [address as `0x${string}`],
          });
          if (!cancelled) setCreationFeeWei(exempt ? 0n : (fee as bigint));
        } catch {
          if (!cancelled) setCreationFeeWei(fee as bigint);
        }
      } else {
        if (!cancelled) setCreationFeeWei(fee as bigint);
      }
    }).catch(() => {
      if (!cancelled) setCreationFeeWei(100_000_000_000_000n); // fallback 0.0001 ETH
    });
    return () => { cancelled = true; };
  }, [factory, address, publicClient]);

  const creationFeeEth = creationFeeWei !== null
    ? Number(creationFeeWei) / 1e18
    : null;

  // ── Oracle pre-flight check (polling, active on review AND deploy stages) ─
  // Reads the oracle address + fallbackOraclePrice from the factory, then
  // attempts latestRoundData() to mirror the contract's own validation.
  // Runs immediately when the user reaches the review step, then repeats
  // every 20 seconds so a sudden oracle outage is caught before they sign —
  // including while a hardware wallet is processing the signature on deploy.
  // The interval is cleared when the component unmounts or the stage changes.
  useEffect(() => {
    if (!factory || (stage !== "review" && stage !== "deploy")) return;

    let cancelled = false;
    setOracleStatus("checking");
    setOracleLastCheckedAt(null);
    setSecondsSinceOracleCheck(0);

    const runCheck = async () => {
      try {
        // Fetch oracle address, fallback price, staleness threshold, and decimals in parallel
        const [oracleAddress, fallbackPrice, stalenessThreshold, oracleDecimalsRaw] = await Promise.all([
          publicClient.readContract({
            address: factory as `0x${string}`,
            abi: fairLaunchFactoryAbi,
            functionName: "priceOracle",
          }),
          publicClient.readContract({
            address: factory as `0x${string}`,
            abi: fairLaunchFactoryAbi,
            functionName: "fallbackOraclePrice",
          }),
          publicClient.readContract({
            address: factory as `0x${string}`,
            abi: fairLaunchFactoryAbi,
            functionName: "oracleStalenessThreshold",
          }),
          publicClient.readContract({
            address: factory as `0x${string}`,
            abi: fairLaunchFactoryAbi,
            functionName: "oracleDecimals",
          }).catch(() => 8 as number), // fallback to 8 if the contract pre-dates this getter
        ]);

        if (cancelled) return;

        // Try the oracle — mirrors the factory's try/catch + validation
        let oracleLive = false;
        try {
          const [roundId, answer, , updatedAt, answeredInRound] =
            await publicClient.readContract({
              address: oracleAddress as `0x${string}`,
              abi: aggregatorAbi,
              functionName: "latestRoundData",
            }) as [bigint, bigint, bigint, bigint, bigint];

          const now = BigInt(Math.floor(Date.now() / 1000));
          const staleness = stalenessThreshold as bigint;
          if (
            answer > 0n &&
            updatedAt <= now &&
            now - updatedAt <= staleness &&
            answeredInRound >= roundId
          ) {
            oracleLive = true;
          }
        } catch {
          // latestRoundData reverted — oracle is offline
        }

        if (cancelled) return;

        const decimals = typeof oracleDecimalsRaw === "number"
          ? oracleDecimalsRaw
          : Number(oracleDecimalsRaw as bigint | number);

        if (oracleLive) {
          setOracleStatus("live");
          setFallbackPriceRaw(null);
        } else if ((fallbackPrice as bigint) > 0n) {
          setOracleStatus("fallback");
          setFallbackPriceRaw(fallbackPrice as bigint);
          setFallbackPriceDecimals(decimals);
        } else {
          setOracleStatus("down");
          setFallbackPriceRaw(null);
        }
        if (!cancelled) setOracleLastCheckedAt(Date.now());
      } catch {
        // Could not read factory state (wrong network / RPC error)
        if (!cancelled) {
          setOracleStatus("unknown");
          setOracleLastCheckedAt(Date.now());
        }
      }
    };

    // Run immediately, then poll every 20 seconds
    void runCheck();
    const intervalId = setInterval(() => { void runCheck(); }, 20_000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [factory, publicClient, stage]);

  // ── "Seconds since last oracle check" ticker ─────────────────────────────
  // Updates every second while on the review stage so the badge timestamp
  // stays current without triggering a new RPC call.
  useEffect(() => {
    if (stage !== "review" || oracleLastCheckedAt === null) return;
    setSecondsSinceOracleCheck(Math.floor((Date.now() - oracleLastCheckedAt) / 1000));
    const tickId = setInterval(() => {
      setSecondsSinceOracleCheck(Math.floor((Date.now() - oracleLastCheckedAt) / 1000));
    }, 1_000);
    return () => clearInterval(tickId);
  }, [stage, oracleLastCheckedAt]);

  // ── Post-deploy: read oracleRoundId from the deployed market ─────────────
  // If the market was created via the fallback path, oracleRoundId is 0.
  // We surface this in the success screen so creators know.
  useEffect(() => {
    if (deployState.status !== "success") return;
    let cancelled = false;

    publicClient.readContract({
      address: deployState.marketAddress,
      abi: bondingCurveMarketAbi,
      functionName: "oracleRoundId",
    }).then((rid) => {
      if (!cancelled) setDeployedOracleRoundId(rid as bigint);
    }).catch(() => {
      // Non-fatal — just omit the warning
    });

    return () => { cancelled = true; };
  }, [deployState, publicClient]);

  // Persist single acknowledgment to sessionStorage (survives step navigation, resets on new session).
  useEffect(() => {
    try { sessionStorage.setItem("synapz_fl_create_ack", String(flAckAccepted)); } catch { /* ignore */ }
  }, [flAckAccepted]);

  const nameRes    = validateName(name);
  const symbolRes  = validateSymbol(symbol);
  const descRes    = validateDescription(description);
  const linksValid = [website, x, telegram].every(u => validateUrl(u).ok);
  const configValid = nameRes.ok && symbolRes.ok && descRes.ok && linksValid;

  const initPrice  = useMemo(calcInitialPrice, []);
  const gradPrice  = useMemo(calcPriceAtGraduation, []);
  const pctSold    = useMemo(calcTokensSoldAtGraduation, []);
  const multiplier = gradPrice / initPrice;

  const idx = STAGES.indexOf(stage);

  // ── Transaction persistence (SYN-WALLET-1) ────────────────────────────────
  const { startTrade } = useTradeRecord({
    walletAddress: address,
    chainId: chainId ?? null,
    action: "deploy",
    contractAddress: factory ?? "0x0000000000000000000000000000000000000000",
  });

  const runDeploy = useCallback(async () => {
    if (!factory || !walletClient || !address || creationFeeWei === null) return;
    if (deployState.status !== "idle" && deployState.status !== "error") return;

    const chainOk = chainId === ACTIVE_CHAIN.chainId;
    if (!chainOk) {
      setDeployState({ status: "error", message: `Wrong network. Please switch to ${ACTIVE_CHAIN.label}.` });
      return;
    }

    // Persistence: generate a txId for this deploy attempt. Fire-and-forget.
    const txId = startTrade({ walletAddress: address!, chainId: chainId! });

    try {
      setDeployState({ status: "estimating" });

      const { request } = await publicClient.simulateContract({
        address: factory as `0x${string}`,
        abi: fairLaunchFactoryAbi,
        functionName: "createFairLaunch",
        args: [name, symbol],
        value: creationFeeWei,
        account: address as `0x${string}`,
      });

      setDeployState({ status: "signing" });
      void persistTradeStatus(txId, "AWAITING_SIGNATURE", {
        walletAddress: address!, chainId: chainId!, action: "deploy",
        contractAddress: factory ?? undefined,
        amountWei: String(creationFeeWei),
      });
      const hash = await walletClient.writeContract(request);
      void persistTradeStatus(txId, "SUBMITTED", { txHash: hash });

      setDeployState({ status: "confirming", hash });
      const receipt = await publicClient.waitForTransactionReceipt({ hash, confirmations: 1 });

      if (receipt.status !== "success") {
        throw new Error("Transaction reverted on-chain.");
      }

      // Parse FairLaunchCreated event from the receipt
      const logs = parseEventLogs({
        abi: fairLaunchFactoryAbi,
        eventName: "FairLaunchCreated",
        logs: receipt.logs,
      });

      if (logs.length === 0) {
        throw new Error("Transaction succeeded but FairLaunchCreated event not found.");
      }

      const logArgs = logs[0]?.args as { token: `0x${string}`; market: `0x${string}` } | undefined;
      if (!logArgs) throw new Error("FairLaunchCreated event args missing from receipt.");
      const { token: tokenAddress, market: marketAddress } = logArgs;

      // Snapshot fallback price at the moment of confirmed deployment so the
      // success screen always shows the price that was actually used, regardless
      // of subsequent oracle polling changes (Task #296).
      setDeployedFallbackPriceRaw(fallbackPriceRaw);
      setDeployedFallbackPriceDecimals(fallbackPriceDecimals);
      setDeployState({ status: "success", tokenAddress, marketAddress, hash });
      void persistTradeStatus(txId, "CONFIRMED", {
        txHash: hash,
        contractAddress: marketAddress,
      });

      // Navigate to the market page after a short delay
      setTimeout(() => {
        navigate(`/fair-launch/market/${ACTIVE_CHAIN.chainId}/${tokenAddress}`);
      }, 2000);

    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      // User rejection
      if (msg.includes("User rejected") || msg.includes("4001") || msg.includes("denied")) {
        void persistTradeStatus(txId, "CANCELLED", { errorMessage: "User rejected" });
        setDeployState({ status: "error", message: "Transaction cancelled." });
      } else {
        void persistTradeStatus(txId, "FAILED", { errorMessage: msg.slice(0, 200) });
        setDeployState({ status: "error", message: msg.slice(0, 200) });
      }
    }
  }, [factory, walletClient, publicClient, address, chainId, creationFeeWei, name, symbol, deployState.status, navigate, startTrade, fallbackPriceRaw, fallbackPriceDecimals]);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <header className="space-y-2">
        <div className="flex items-center gap-3">
          <Link to="/fair-launch" className="text-sm text-muted hover:text-plasma transition-colors">← Fair Launch</Link>
        </div>
        <h1 className="font-display text-3xl font-bold">Create a Fair Launch</h1>
        {/* Stepper */}
        <ol className="flex flex-wrap gap-2 text-xs mt-3">
          {STAGES.map((s, i) => (
            <li key={s} className={`flex items-center gap-1.5 rounded-full border px-3 py-1 capitalize font-medium ${
              i === idx ? "border-plasma/60 text-plasma bg-plasma/10"
              : i < idx ? "border-line text-ink bg-line/30"
              : "border-line/50 text-muted"
            }`}>
              <span className={`inline-flex w-4 h-4 items-center justify-center rounded-full text-[10px] font-bold ${
                i === idx ? "bg-plasma text-void" : i < idx ? "bg-plasma/40 text-void" : "bg-line/60 text-muted"
              }`}>{i + 1}</span>
              {s}
            </li>
          ))}
        </ol>
        <div className="h-[2px] bg-line rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-plasma to-plasma2 rounded-full transition-all duration-500"
            style={{ width: `${(idx / (STAGES.length - 1)) * 100}%` }} />
        </div>
      </header>

      {/* ── CONNECT ── */}
      {stage === "connect" && (
        <Card className="space-y-4">
          <h2 className="font-display text-lg">1 · Connect your wallet</h2>
          <p className="text-sm text-muted">
            Connect a wallet on {ACTIVE_CHAIN.label}. You sign every transaction yourself.
          </p>
          <WalletButton />
          {ACTIVE_CHAIN.faucetUrl && (
            <a className="text-sm text-plasma hover:underline" href={ACTIVE_CHAIN.faucetUrl}
              target="_blank" rel="noreferrer">Get testnet ETH ↗</a>
          )}
          <div className="flex justify-end">
            <Button disabled={!isConnected} onClick={() => setStage("configure")}>Continue</Button>
          </div>
        </Card>
      )}

      {/* ── CONFIGURE ── */}
      {stage === "configure" && (
        <div className="space-y-4">
          <Card className="space-y-4">
            <h2 className="font-display text-lg">2 · Configure</h2>
            <Field label="Token name" error={name ? nameRes.error : undefined}
              hint={`${byteLength(name)}/${MAX_NAME_BYTES} bytes`}>
              <input className={inputClass} value={name} onChange={e => setName(e.target.value)}
                placeholder="My Fair Token" />
            </Field>
            <Field label="Symbol" error={symbol ? symbolRes.error : undefined}
              hint={`${byteLength(symbol)}/${MAX_SYMBOL_BYTES} bytes`}>
              <input className={inputClass} value={symbol} onChange={e => setSymbol(e.target.value)}
                placeholder="MFT" />
            </Field>
            <Field label="Description (optional)" error={description ? descRes.error : undefined}
              hint={`${description.length}/${MAX_DESCRIPTION_CHARS}`}>
              <textarea className={inputClass} rows={3} value={description}
                onChange={e => setDescription(e.target.value)} />
            </Field>
            <Field label="Website (optional)" error={website ? validateUrl(website).error : undefined}>
              <input className={inputClass} value={website} onChange={e => setWebsite(e.target.value)}
                placeholder="https://…" />
            </Field>
            <Field label="X (optional)" error={x ? validateUrl(x).error : undefined}>
              <input className={inputClass} value={x} onChange={e => setX(e.target.value)}
                placeholder="https://x.com/…" />
            </Field>
            <Field label="Telegram (optional)">
              <input className={inputClass} value={telegram} onChange={e => setTelegram(e.target.value)}
                placeholder="https://t.me/…" />
            </Field>
          </Card>

          {/* Curve parameters (read-only) */}
          <Card className="space-y-3">
            <h3 className="font-display text-sm font-semibold text-muted uppercase tracking-wide">
              Bonding curve parameters (fixed)
            </h3>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 space-y-2 text-xs">
                {[
                  ["Total supply", `${TOTAL_SUPPLY.toLocaleString()} tokens`],
                  ["Curve allocation", `${CURVE_ALLOC_PCT}% (${(Number(TOTAL_SUPPLY) * CURVE_ALLOC_PCT / 100).toLocaleString()})`],
                  ["Migration allocation", `${MIGRATION_PCT}% (to DEX at graduation)`],
                  ["Virtual ETH reserve", `~${VIRTUAL_ETH} ETH (oracle-set at launch)`],
                  ["Graduation target", `~$${GRAD_THRESHOLD_USD.toLocaleString()} USD (Chainlink oracle)`],
                  ["Initial price", `~${(initPrice * 1e9).toFixed(4)} nETH/token`],
                  ["Price at graduation", `~${(gradPrice * 1e9).toFixed(4)} nETH/token`],
                  ["Price multiplier", `~${multiplier.toFixed(1)}×`],
                  ["Tokens sold at graduation", `~${pctSold.toFixed(1)}%`],
                  ["Buy / sell fee", `${BUY_FEE_BPS / 100}% / ${SELL_FEE_BPS / 100}%`],
                  ["LP lock duration", `${LOCK_DAYS} days`],
                  ["Creation fee", creationFeeEth !== null ? `${creationFeeEth} ETH` : "loading…"],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2 py-1 border-b border-line/20">
                    <span className="text-muted">{k}</span>
                    <span className="font-mono text-plasma text-right">{v}</span>
                  </div>
                ))}
              </div>
              <div className="sm:w-44 flex flex-col items-center gap-1">
                <CurvePreview ethRaisedPreview={0} />
                <p className="text-[9px] text-muted/50 font-mono text-center">price curve (approx. @ $2k/ETH)</p>
              </div>
            </div>
            <p className="text-[10px] text-muted/60 pt-1">
              Virtual ETH reserve and graduation ETH threshold are computed from the Chainlink
              ETH/USD oracle at the moment you deploy. Later oracle movement does not affect
              an existing market.
            </p>
          </Card>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStage("connect")}>Back</Button>
            <Button disabled={!configValid} onClick={() => setStage("review")}>Review</Button>
          </div>
        </div>
      )}

      {/* ── REVIEW ── */}
      {stage === "review" && (
        <div className="space-y-4">
          <Card className="space-y-3">
            <h2 className="font-display text-lg">3 · Review</h2>
            <dl className="text-sm divide-y divide-line">
              {[
                ["Token name", name],
                ["Symbol", symbol],
                ["Total supply", `${TOTAL_SUPPLY.toLocaleString()} (fixed)`],
                ["Curve allocation", `${CURVE_ALLOC_PCT}% — bonding curve`],
                ["Migration allocation", `${MIGRATION_PCT}% — DEX at graduation`],
                ["Creator gets", "0% — you buy through the curve"],
                ["Creator (wallet)", address ?? "—"],
                ["Network", `${ACTIVE_CHAIN.label} (${ACTIVE_CHAIN.chainId})`],
                ["Creation fee", creationFeeEth !== null ? `${creationFeeEth} ETH` : "loading…"],
                ["Graduation target", `~$${GRAD_THRESHOLD_USD.toLocaleString()} USD (oracle-set at launch)`],
              ].map(([label, value]) => (
                <div key={label} className="flex items-center justify-between py-2 gap-4">
                  <dt className="text-muted shrink-0">{label}</dt>
                  <dd className="text-ink text-right font-mono break-all text-xs">{value}</dd>
                </div>
              ))}
            </dl>
          </Card>
          {description && (
            <Card className="space-y-1">
              <p className="text-xs text-muted font-semibold">Description</p>
              <p className="text-sm text-ink">{description}</p>
            </Card>
          )}
          <Card className="space-y-2 text-xs text-warn">
            <p>This creates a bonding-curve market — the token supply is NOT delivered to your wallet.</p>
            <p>You must buy through the curve like every other participant.</p>
            <p>Once deployed, the token and market parameters are immutable.</p>
            <p>The graduation ETH threshold is computed from the Chainlink oracle at deploy time
               and stored immutably — it will not change after your market is created.</p>
          </Card>

          {/* ── Oracle status badge — shown for all states (Tasks #142, #143, #144) ── */}
          {(() => {

            if (oracleStatus === "checking") {
              return (
                <div className="rounded-lg border border-muted/30 bg-muted/10 p-3 text-xs text-muted flex items-center gap-2">
                  <svg className="animate-spin h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  <span>Checking oracle status…</span>
                </div>
              );
            }

            if (oracleStatus === "live") {
              return (
                <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-3 text-xs text-green-400 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-base leading-none">✓</span>
                    <span><strong>Oracle live</strong> — graduation threshold will be set from the live Chainlink ETH/USD price at deployment time.</span>
                  </div>
                  <p className="text-green-400/60 pl-5">Last checked {secondsSinceOracleCheck}s ago</p>
                </div>
              );
            }

            if (oracleStatus === "fallback") {
              return (
                <div className="rounded-lg border border-warn/50 bg-warn/10 p-3 space-y-1.5 text-xs text-warn">
                  <p className="font-semibold flex items-center gap-1.5">
                    <span>⚠</span> Oracle offline — emergency fallback price active
                  </p>
                  {fallbackPriceRaw !== null && (
                    <p>
                      Fallback price:{" "}
                      <strong className="font-mono">{formatFallbackPrice(fallbackPriceRaw, fallbackPriceDecimals)}</strong>
                      {" "}— set by the protocol operator.
                    </p>
                  )}
                  <p>
                    The Chainlink ETH/USD oracle is currently unresponsive. The factory will use
                    the operator-set <strong>emergency fallback price</strong> to derive your
                    market's graduation threshold.
                  </p>
                  <p>
                    Your market will launch successfully, but the graduation target will be
                    based on the fallback price rather than the live oracle. The{" "}
                    <strong>oracleRoundId</strong> stored on-chain will be <strong>0</strong>,
                    marking this market as created during an oracle outage.
                  </p>
                  <p className="text-warn/60">Last checked {secondsSinceOracleCheck}s ago</p>
                </div>
              );
            }

            if (oracleStatus === "down") {
              return (
                <div className="rounded-lg border border-danger/50 bg-danger/10 p-3 space-y-1.5 text-xs text-danger">
                  <p className="font-semibold flex items-center gap-1.5">
                    <span>⛔</span> Oracle offline — creation will fail
                  </p>
                  <p>
                    The Chainlink oracle is offline and <strong>no emergency fallback price</strong> has been
                    configured. Market creation will revert on-chain until the oracle recovers or the
                    operator sets a fallback price.
                  </p>
                  <p className="font-semibold">
                    Do not proceed. Please try again later.
                  </p>
                  <p className="text-danger/60 font-normal">Last checked {secondsSinceOracleCheck}s ago</p>
                </div>
              );
            }

            if (oracleStatus === "unknown") {
              return (
                <div className="rounded-lg border border-muted/30 bg-muted/10 p-3 text-xs text-muted space-y-1">
                  <div className="flex items-center gap-2">
                    <span>?</span>
                    <span>Oracle status could not be determined (RPC error). Proceed with caution — creation may revert on-chain.</span>
                  </div>
                  <p className="text-muted/60 pl-4">Last checked {secondsSinceOracleCheck}s ago</p>
                </div>
              );
            }

            return null;
          })()}

          {/* ── One-click disclosure acknowledgment (required before proceeding) ── */}
          <Card className="space-y-3">
            <p className="text-xs font-semibold text-muted">Acknowledgment required</p>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="fl-create-ack-checkbox">
              <input
                type="checkbox"
                className="mt-1 shrink-0 accent-plasma"
                checked={flAckAccepted}
                onChange={(e) => setFlAckAccepted(e.target.checked)}
              />
              <span className="text-sm text-muted leading-relaxed">
                I have read the{" "}
                <Link to="/legal/smart-contract-risk" target="_blank" rel="noreferrer" className="underline text-plasma hover:opacity-80">
                  Risk Disclosure
                </Link>{" "}
                and{" "}
                <Link to="/legal/irreversible-transactions" target="_blank" rel="noreferrer" className="underline text-plasma hover:opacity-80">
                  Irreversible Transaction Warning
                </Link>
                . I understand this platform is in{" "}
                <Link to="/legal/unaudited-beta" target="_blank" rel="noreferrer" className="underline text-plasma hover:opacity-80">
                  unaudited beta
                </Link>
                , all blockchain transactions are irreversible, I receive 0 tokens at launch,
                and market parameters are immutable once deployed.
              </span>
            </label>
          </Card>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStage("configure")}>Back</Button>
            <Button
              disabled={oracleStatus === "down" || !flAckAccepted}
              onClick={() => setStage("deploy")}
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {/* ── DEPLOY ── */}
      {stage === "deploy" && (
        <Card className="space-y-4">
          <h2 className="font-display text-lg">4 · Deploy</h2>

          {deployState.status === "success" ? (
            <div className="space-y-3">
              <Pill tone="plasma">Market deployed!</Pill>
              <p className="text-sm text-muted">Your fair-launch market is live on-chain.</p>

              {/* Fallback oracle warning on success screen — shown when oracleRoundId === 0 */}
              {deployedOracleRoundId === 0n && (
                <div className="rounded-lg border border-warn/50 bg-warn/10 p-3 space-y-1.5 text-xs text-warn">
                  <p className="font-semibold flex items-center gap-1.5">
                    <span>⚠</span>{" "}
                    Emergency fallback price used
                    {deployedFallbackPriceRaw !== null && (
                      <span className="font-mono font-normal">
                        : {formatFallbackPrice(deployedFallbackPriceRaw, deployedFallbackPriceDecimals)}
                      </span>
                    )}
                  </p>
                  <p>
                    The Chainlink oracle was offline at the moment your market was created.
                    The graduation threshold was derived from the operator-set emergency
                    fallback ETH/USD price instead of a live oracle feed.
                  </p>
                  <p>
                    Your market is live and fully functional. The graduation target is
                    locked immutably on-chain. A <strong>⚠ Fallback Oracle</strong> badge
                    will appear on your market's page to inform traders.
                  </p>
                </div>
              )}

              <div className="rounded-lg border border-line/50 bg-panel2/30 p-4 space-y-2 text-xs">
                <div className="flex justify-between gap-2">
                  <span className="text-muted">Token</span>
                  <span className="font-mono text-ink break-all">{deployState.tokenAddress}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted">Market</span>
                  <span className="font-mono text-ink break-all">{deployState.marketAddress}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted">Tx</span>
                  <a href={`${ACTIVE_CHAIN.explorerUrl}/tx/${deployState.hash}`}
                    className="font-mono text-plasma hover:underline break-all"
                    target="_blank" rel="noreferrer">
                    {deployState.hash.slice(0, 18)}…
                  </a>
                </div>
              </div>
              <p className="text-xs text-muted/60">Redirecting to your market page…</p>
            </div>
          ) : (
            <>
              {/* ── Fallback oracle warning in deploy step ── */}
              {(oracleStatus === "fallback" || oracleStatus === "down") && (
                <div className={`rounded-lg border p-3 space-y-1.5 text-xs ${
                  oracleStatus === "down"
                    ? "border-danger/50 bg-danger/10 text-danger"
                    : "border-warn/50 bg-warn/10 text-warn"
                }`}>
                  <p className="font-semibold flex items-center gap-1.5">
                    <span>⚠</span>
                    {oracleStatus === "down"
                      ? "Oracle offline — deployment will fail"
                      : "Oracle offline — emergency fallback price will be used"}
                  </p>
                  {oracleStatus === "fallback" ? (
                    <p>
                      The Chainlink oracle is currently unresponsive. If you proceed,
                      the factory will use the operator's <strong>emergency fallback price</strong> to
                      set the graduation threshold. Your market will launch successfully but
                      will carry a <strong>⚠ Fallback Oracle</strong> badge visible to traders.
                    </p>
                  ) : (
                    <p>
                      The Chainlink oracle is offline and no emergency fallback has been
                      configured. Deployment will revert. Please try again later.
                    </p>
                  )}
                </div>
              )}

              {/* ── Unknown oracle status caution in deploy step (Task #294) ── */}
              {oracleStatus === "unknown" && (
                <div className="rounded-lg border border-warn/50 bg-warn/10 p-3 space-y-1.5 text-xs text-warn">
                  <p className="font-semibold flex items-center gap-1.5">
                    <span>⚠</span> Oracle status could not be determined
                  </p>
                  <p>
                    An RPC error prevented the oracle state from being read.
                    Proceed with caution — the transaction may revert if the oracle
                    is offline at deployment time. Try again later if you want to
                    confirm the oracle is live before deploying.
                  </p>
                </div>
              )}

              <div className="rounded-lg border border-line/50 bg-panel2/30 p-4 space-y-2 text-xs text-muted">
                <p className="font-semibold text-ink/80">What happens when you deploy:</p>
                <ol className="list-decimal list-inside space-y-1 pl-2">
                  <li>Chainlink oracle is read for the current ETH/USD price</li>
                  <li>1B tokens minted; 80% + 20% sent to a new BondingCurveMarket</li>
                  <li>Graduation threshold locked immutably (~$50,000 USD in ETH)</li>
                  <li>You receive 0 tokens — buy through the curve when it opens</li>
                  <li>Trading starts immediately after deployment</li>
                  <li>When the graduation target is reached, the market migrates to DEX</li>
                </ol>
              </div>

              <div className="rounded-lg border border-line/50 bg-panel2/30 p-3 text-xs text-muted/70 space-y-1">
                <p className="font-semibold text-ink/60">Testnet disclaimer</p>
                <p>This is Robinhood Chain Testnet. All tokens and ETH have no real monetary value.
                   Migration uses a simulation adapter — no real DEX liquidity is created.</p>
              </div>

              {chainId !== ACTIVE_CHAIN.chainId && (
                <Pill tone="danger">Wrong network — switch to {ACTIVE_CHAIN.label}</Pill>
              )}

              {deployState.status === "error" && (
                <div className="rounded border border-danger/40 bg-danger/10 p-3 text-xs text-danger">
                  {deployState.message}
                  <button className="ml-2 underline" onClick={() => setDeployState({ status: "idle" })}>
                    Try again
                  </button>
                </div>
              )}

              {(deployState.status === "confirming") && (
                <div className="text-xs text-muted">
                  Confirming…{" "}
                  <a href={`${ACTIVE_CHAIN.explorerUrl}/tx/${deployState.hash}`}
                    className="text-plasma hover:underline font-mono"
                    target="_blank" rel="noreferrer">
                    {deployState.hash.slice(0, 18)}…
                  </a>
                </div>
              )}

              {/* ── Insufficient ETH for creation fee ── */}
              {!showAddEthCreate &&
                balance !== null &&
                creationFeeWei !== null &&
                balance < creationFeeWei &&
                deployState.status === "idle" && (
                  <div className="space-y-2">
                    <Pill tone="danger">Insufficient ETH for creation fee</Pill>
                    <p className="text-xs text-muted/70">
                      You need{" "}
                      <span className="font-mono">
                        {((Number(creationFeeWei) - Number(balance)) / 1e18).toFixed(6)} ETH
                      </span>{" "}
                      more on Robinhood Chain to pay the creation fee.
                    </p>
                    <Button
                      variant="ghost"
                      className="w-full text-xs"
                      onClick={() => setShowAddEthCreate(true)}
                      data-testid="open-add-eth-create"
                    >
                      Add ETH to Robinhood Chain →
                    </Button>
                  </div>
                )}
              {showAddEthCreate && (
                <AddEthPanel
                  wallet={address}
                  currentL2Balance={balance}
                  shortfallWei={
                    creationFeeWei !== null && balance !== null && creationFeeWei > balance
                      ? creationFeeWei - balance
                      : null
                  }
                  returnContext="create"
                  onClose={() => setShowAddEthCreate(false)}
                  onFundsArrived={() => setShowAddEthCreate(false)}
                />
              )}

              <div className="flex justify-between items-center">
                <Button variant="ghost" onClick={() => setStage("review")}
                  disabled={deployState.status === "signing" || deployState.status === "confirming"}>
                  Back
                </Button>
                <Button
                  disabled={
                    !factory ||
                    !isConnected ||
                    chainId !== ACTIVE_CHAIN.chainId ||
                    oracleStatus === "down" ||
                    deployState.status === "estimating" ||
                    deployState.status === "signing" ||
                    deployState.status === "confirming" ||
                    (balance !== null && creationFeeWei !== null && balance < creationFeeWei)
                  }
                  onClick={runDeploy}
                >
                  {deployState.status === "estimating" ? "Estimating…"
                   : deployState.status === "signing"   ? "Sign in wallet…"
                   : deployState.status === "confirming" ? "Confirming…"
                   : `Deploy — pay ${creationFeeEth !== null ? `${creationFeeEth} ETH` : "…"}`}
                </Button>
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
}
